import {
  Button,
  Box,
  CircularProgress,
  Typography,
  FormControl,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Link,
} from "@mui/material";

import * as React from "react";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import jwtDecode from "jwt-decode";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import usersEndpoints from "../../../services/userServices";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
/*global chrome*/
/*global google*/

/* ── Shared dark-field style ────────────────────────────── */
const darkFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor:      "rgba(255,255,255,0.05)",
    borderRadius: "10px",
    color:        "white",
    "& fieldset":             { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset":       { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FF6B35", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": {
    color: "white",
    "&::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
  },
  "& .MuiFormHelperText-root": { color: "#ff6060", fontSize: "12px", ml: 0 },
  "& .MuiInputAdornment-root .MuiIconButton-root": { color: "rgba(255,255,255,0.45)" },
};

const orangeBtnSx = {
  background:    "linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)",
  color:         "white",
  fontWeight:    700,
  fontSize:      "15px",
  py:            1.6,
  borderRadius:  "10px",
  textTransform: "none",
  width:         "100%",
  "&:hover": {
    background: "linear-gradient(135deg, #e85520 0%, #cc3300 100%)",
    boxShadow:  "0 8px 30px rgba(255,107,53,0.40)",
  },
  transition: "all 0.2s ease",
};

function SigninSection() {
  const [cookies, setCookie] = useCookies(["user", "auth", "logindata"]);

  const [passwordType, setPasswordType] = useState("password");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
  } = useForm();

  const [loginMode, setLoginMode] = useState("otp");
  const [otpSent, setOtpSent] = useState(false);
  const [loaders, setLoaders] = useState({
    password_login_btn: false,
    send_otp_btn: false,
    verify_otp_btn: false,
  });

  const [redirectText, setRedirectText] = useState("");

  const SPACE_KEY = " ";
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_ID_SIGNIN;

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {});
    return () => {
      subscription.unsubscribe();
    };
  }, [watch]);

  const handleGoogleLoginResponse = async (response) => {
    if (response.credential) {
      const details = jwtDecode(response.credential);
      if (details.hd) {
        let headers = { headers: { "user-auth": response.credential } };
        const postdata = { mode: "gauth", page: "signin" };
        const loginData = await usersEndpoints.signInUser(postdata, headers);
        saveLoginData(loginData);
      } else {
        toast.error("Please join using a business email address");
      }
    }
  };

  useEffect(() => {
    window.onGoogleLibraryLoad = () => {
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (data) => handleGoogleLoginResponse(data),
        cancel_on_tap_outside: false,
      });

      google.accounts.id.renderButton(
        document.getElementById("google_sign_in"),
        {
          theme: "filled_blue",
          size: "large",
          text: "continue_with",
        }
      );

      google.accounts.id.prompt();
    };
  }, []);

  const onSubmit = (data) => {
    handleSignin(data);
  };

  const showInvalidValues = (data) => {};

  const handleChangePasswordType = () => {
    let type_attr = "";
    if (passwordType === "password") {
      type_attr = "text";
    } else {
      type_attr = "password";
    }
    setPasswordType(type_attr);
  };

  const handleSpaceInInput = (event) => {
    let keyCode = event.keyCode || event.which;
    keyCode = String.fromCharCode(keyCode);
    if (keyCode === SPACE_KEY) {
      event.returnValue = false;
      if (event.preventDefault) {
        event.preventDefault();
      }
    }
  };

  const handleSignin = async (signindata) => {
    let postdata = {
      email: signindata.user_email,
      password: signindata.user_password ? signindata.user_password : "",
      otp_entered: signindata.otp_entered ? signindata.otp_entered : "",
      mode: loginMode,
    };

    if (loginMode === "normal") {
      setLoaders({ ...loaders, ...{ password_login_btn: true } });
    } else if (loginMode === "otp") {
      setLoaders({ ...loaders, ...{ verify_otp_btn: true } });
    }

    const response = await usersEndpoints.signInUser(postdata);

    setLoaders({
      ...loaders,
      ...{ password_login_btn: false, verify_otp_btn: false },
    });

    saveLoginData(response);
  };

  const saveLoginData = (response) => {
    if (response) {
      if (response["success"] === true) {
        setCookie(
          "user",
          {
            name: response["loginData"]["fullname"],
            _id: response["loginData"]["user_id"],
            credits: response["loginData"]["credits"],
          },
          { path: "/", secure: false, maxAge: 2592000 }
        );
        setCookie(
          "user",
          {
            name: response["loginData"]["fullname"],
            _id: response["loginData"]["user_id"],
            credits: response["loginData"]["credits"],
          },
          { path: "/filter", secure: false, maxAge: 2592000 }
        );
        setCookie(
          "auth",
          { token: response["loginData"]["jwttoken"] },
          { path: "/", secure: false, maxAge: 2592000 }
        );

        try {
          chrome.runtime.sendMessage(
            process.env.REACT_APP_EXTENSION_ID,
            { message: "login_successful", loginData: response["loginData"] },
            function (responseFromExtension) {
              responseFromExtension = responseFromExtension || {};
              window.close();
            }
          );
        } catch (error) {
          console.log(error);
          setCookie("logindata", response["loginData"], {
            path: "/",
            secure: false,
            maxAge: 2592000,
          });
        }
      } else {
        toast.error(response["message"]);
        if (response["redirect"]) {
          createRedirectTimer(response["redirect"], 4000);
        }
      }
    }
  };

  const handleLoginModeChange = (value) => {
    setLoginMode(value);
    setOtpSent(false);
  };

  const sendOtpToUser = async () => {
    const user_email = getValues("user_email");
    clearErrors("user_email");
    if (user_email.length === 0) {
      setError(
        "user_email",
        { message: "Please enter your registered email address" },
        { shouldFocus: true }
      );
      return;
    }

    let postdata = { email: user_email };
    setLoaders({ ...loaders, ...{ send_otp_btn: true } });
    const response = await usersEndpoints.sendLoginOTP(postdata);
    setLoaders({ ...loaders, ...{ send_otp_btn: false } });

    if (response["success"] === true) {
      setOtpSent(true);
    } else {
      toast.error(response["message"]);
      if (response["redirect"]) {
        createRedirectTimer(response["redirect"], 4000);
      }
    }
  };

  const createRedirectTimer = (path, time) => {
    if (!path) path = "/";
    if (time === 0) {
      window.location.href = path;
    }
    setTimeout(() => {
      setRedirectText(`Redirecting in ${time / 1000} seconds`);
      time = time - 1000;
      createRedirectTimer(path, time);
    }, 1000);
  };

  return (
    <>
      {/* ── Header ── */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography
          variant="h5"
          component="h5"
          sx={{ fontWeight: 700, color: "white", mb: 0.75, fontSize: "21px" }}
        >
          Welcome back
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.38)" }}>
          Don't have an account?{" "}
          <Link
            href="/authenticate/signup"
            sx={{
              color: "#FF6B35",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Sign up free
          </Link>
        </Typography>
      </Box>

      {/* ── Google SSO (prominent) ── */}
      <Stack id="google_sign_in" alignItems="center" sx={{ mb: 2.5, minHeight: "44px" }} />

      {/* ── OR divider ── */}
      <Divider
        sx={{
          mb: 2.5,
          "&::before, &::after": { borderColor: "rgba(255,255,255,0.09)" },
          color:         "rgba(255,255,255,0.28)",
          fontSize:      "11px",
          fontWeight:    500,
          letterSpacing: "0.04em",
        }}
      >
        or continue with email
      </Divider>

      {/* ── Login mode toggle (OTP / Password) ── */}
      <Box
        sx={{
          display:      "flex",
          bgcolor:      "rgba(255,255,255,0.05)",
          borderRadius: "10px",
          p:            "4px",
          mb:           2.5,
        }}
      >
        {[
          { value: "otp",    label: "Magic OTP"  },
          { value: "normal", label: "Password"   },
        ].map(({ value, label }) => (
          <Box
            key={value}
            onClick={() => handleLoginModeChange(value)}
            sx={{
              flex:         1,
              textAlign:    "center",
              py:           "8px",
              borderRadius: "8px",
              fontSize:     "13px",
              fontWeight:   600,
              cursor:       "pointer",
              transition:   "all 0.18s ease",
              userSelect:   "none",
              bgcolor:      loginMode === value ? "rgba(255,107,53,0.14)" : "transparent",
              color:        loginMode === value ? "#FF6B35"               : "rgba(255,255,255,0.42)",
              border:       loginMode === value ? "1px solid rgba(255,107,53,0.28)" : "1px solid transparent",
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {/* ── Form ── */}
      <form onSubmit={handleSubmit(onSubmit, showInvalidValues)}>

        {/* Email */}
        <FormControl sx={{ width: "100%", mb: 2 }}>
          <TextField
            variant="outlined"
            id="email_address_input"
            placeholder="Work email address"
            autoFocus
            {...register("user_email", {
              required: { value: true, message: "Email field cannot be empty" },
              pattern: {
                value: /^(?!.+@(gmail|google|yahoo|outlook|hotmail|msn)\..+)(.+@.+\..+)$/,
                message: "Please provide a business email address",
              },
            })}
            error={Boolean(errors.user_email)}
            helperText={errors.user_email?.message}
            autoComplete={"off"}
            onKeyPress={handleSpaceInInput}
            sx={darkFieldSx}
          />
        </FormControl>

        {/* Password mode */}
        {loginMode === "normal" && (
          <>
            <FormControl sx={{ width: "100%", mb: 0 }}>
              <TextField
                variant="outlined"
                placeholder="Your password"
                type={passwordType}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleChangePasswordType}
                        sx={{ color: "rgba(255,255,255,0.45)" }}
                      >
                        {passwordType === "password" ? (
                          <RemoveRedEyeOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register("user_password", {
                  required: "Please enter your password",
                })}
                error={Boolean(errors.user_password)}
                helperText={errors.user_password?.message}
                autoComplete={"off"}
                onKeyPress={handleSpaceInInput}
                sx={darkFieldSx}
              />
            </FormControl>

            {/* Forgot password */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, mt: 0.5 }}>
              <Link
                href="/authenticate/reset-password/"
                sx={{
                  fontSize:       "12px",
                  color:          "rgba(255,255,255,0.38)",
                  textDecoration: "none",
                  "&:hover":      { color: "#FF6B35" },
                  transition:     "color 0.15s ease",
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              endIcon={
                <CircularProgress
                  size="0.9rem"
                  sx={{ color: "white", display: loaders.password_login_btn ? "block" : "none" }}
                />
              }
              sx={{ ...orangeBtnSx, mb: 2 }}
            >
              Sign in
            </Button>
          </>
        )}

        {/* OTP mode */}
        {loginMode === "otp" && (
          <>
            {otpSent ? (
              <>
                <FormControl sx={{ width: "100%", mb: 2 }}>
                  <TextField
                    variant="outlined"
                    placeholder="Enter OTP sent to your email"
                    type="tel"
                    autoComplete={"off"}
                    onKeyPress={handleSpaceInInput}
                    inputProps={{ maxLength: 6 }}
                    {...register("otp_entered", {
                      required: "Please enter the OTP sent to your email",
                    })}
                    error={Boolean(errors.otp_entered)}
                    helperText={errors.otp_entered?.message}
                    sx={darkFieldSx}
                  />
                </FormControl>
                <Button
                  type="submit"
                  endIcon={
                    <CircularProgress
                      size="0.9rem"
                      sx={{ color: "white", display: loaders.verify_otp_btn ? "block" : "none" }}
                    />
                  }
                  sx={{ ...orangeBtnSx, mb: 2 }}
                >
                  Verify &amp; sign in
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={sendOtpToUser}
                endIcon={
                  <CircularProgress
                    size="0.9rem"
                    sx={{ color: "white", display: loaders.send_otp_btn ? "block" : "none" }}
                  />
                }
                sx={{ ...orangeBtnSx, mb: 2 }}
              >
                Send OTP
              </Button>
            )}
          </>
        )}
      </form>

      {/* ── Redirect countdown ── */}
      {redirectText && (
        <Typography
          fontSize="small"
          textAlign="center"
          sx={{ color: "#ff6060", fontWeight: 600, mt: 1 }}
        >
          {redirectText}
        </Typography>
      )}

      <ToastContainer
        style={{ fontSize: "0.85rem" }}
        hideProgressBar={true}
        autoClose={2000}
      />
    </>
  );
}

export default SigninSection;
